import { FormattedMessage } from 'react-intl';

import { useLocale } from '../lang/LocalizationProvider';
import Divider from './Divider';

import styles from './SelectedPlanBlock.module.scss';

import { type Plan } from '@/utils/mapProductObject';

export interface SelectedPlanBlockProps {
  selectedPlan: Plan;
  couponDiscountPercentage?: number | string;
}

const SelectedPlanBlock = ({ selectedPlan, couponDiscountPercentage }: SelectedPlanBlockProps) => {
  const { selectedLocalization } = useLocale();

  if (selectedPlan) {
    return (
      <>
        <Divider spacing={24} />

        <div className={styles.planLabels}>
          <span>
            <FormattedMessage
              id="layout.selectedPlan.label"
              defaultMessage="Your plan"
              description="Label preceding selected plan name"
            />
          </span>
          {selectedPlan.hasTrial && (
            <span>
              <FormattedMessage
                id="layout.selectedPlan.trialLabel"
                description="Trial label for the selected plan"
                values={{ trialPeriodLengthDays: selectedPlan.trialDays }}
                defaultMessage="{trialPeriodLengthDays, plural,
                  one {24-hour}
                  other {#-day}
                } free trial, then"
              />
            </span>
          )}
        </div>
        <div className={styles.planDetails}>
          <span>{selectedPlan.name[selectedLocalization]}</span>
          <span className={styles.planDetailsPrice}>
            <FormattedMessage
              id="layout.selectedPlan.cost"
              defaultMessage="{totalPrice} billed once per {renewPeriod}"
              description="Cost of selected plan alongside billing period frequency"
              values={{
                totalPrice: <b>{selectedPlan.orderTotal}</b>,
                renewPeriod: selectedPlan.name[selectedLocalization].toLocaleLowerCase(),
              }}
            />
          </span>
        </div>

        {selectedPlan.taxes.map((tax) => (
          <div key={tax.name} className={styles.taxLine}>
            <FormattedMessage
              id="layout.selectedPlan.taxLine"
              defaultMessage="{taxAmount} {taxName} included"
              description="Added line to total price calculation, describing tax addition"
              values={{ taxAmount: tax.amount, taxName: tax.name }}
            />
          </div>
        ))}

        {couponDiscountPercentage && (
          <div className={styles.discountWrapper}>
            <div className={styles.discountContainer}>
              <span className={styles.discountLabel}>
                <FormattedMessage
                  id="layout.selectedPlan.couponDiscount"
                  defaultMessage="{discountPercentage, number}% discount is applied"
                  description="Label shown to user, when discount is applied to selecetd plan"
                  values={{ discountPercentage: couponDiscountPercentage }}
                />
              </span>
            </div>
          </div>
        )}

        <Divider spacing={24} />
      </>
    );
  }

  return null;
};

export default SelectedPlanBlock;
